import {inject, Inject, Injectable, signal} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {SortDirectionValuesModel} from "../models/SortDirectionValues-model";
import {WinOlsTransactionModel} from "../models/WinOlsTransaction-model";
import {NotificationService} from "./Notification.service";
import { Router} from "@angular/router";
import {firstValueFrom, forkJoin, map, Observable} from "rxjs";
import {WinOlsStatisticModel} from "../models/WinOlsStatisticModel";



interface WinOlsTransactionPage {
    content: WinOlsTransactionModel[];
    last?: boolean;
}

export type WinOlsStatusFilter = 'SOLUTION_OFFERED' | 'COMPLETED' | 'PRE_CHECK' | 'REQUESTED' | 'ERROR_OR_NO_OPTIONS';


@Injectable({
    providedIn: 'root'
})
export class WinOlsTransactionService {

    private url: string = `${this.API_URL}`;

    winOlsTransactions = signal<WinOlsTransactionModel[]>([]);
    winOlsTransactionItem = signal<WinOlsTransactionModel>(new WinOlsTransactionModel());
    public winOlsStatistic = signal<WinOlsStatisticModel[]>([]);
    displayedTableHeaders = signal<string[]>([]);
    displayedColumns = signal<(keyof WinOlsTransactionModel)[]>([]);
    pageNumber = signal<number>(0);
    pageSize = signal<number>(50);
    isLoading = signal<boolean>(false);
    selectedDateRange = signal<{startDate: string; endDate: string} | null>(null);
    selectedStatuses = signal<string[]>([]);
    sortDirectionValues = signal<SortDirectionValuesModel>({active: "id", direction: "desc"});
    private hasMorePages = true;
    private isFetchingPage = false;
    private activeTableRequestId = 0;
    private currentTransactionId: number | null = null;
    public selectedTimeFrame = signal<string | undefined>('');
    filesToDownload = signal<number[]>([]);
    public total = signal<number | undefined>(0);
    public preCheck = signal<number | undefined>(0);
    public requested = signal<number | undefined>(0);
    public solutionOffered = signal<number | undefined>(0);
    public noAvailableOptions = signal<number | undefined>(0);
    public completed = signal<number | undefined>(0);
    public error = signal<number | undefined>(0);
    public status!: string;
    public startDate: Date;
    public endDate: Date;

    notificationService = inject(NotificationService);
    router = inject(Router);

    constructor(@Inject('API_URL') private API_URL: string, private httpClient: HttpClient) {
    }

    getAllTableHeaderNames() {
        let objectKeys = Object.keys(this.winOlsTransactionItem());
        for (let key of objectKeys) {
            this.displayedColumns().push(key as keyof WinOlsTransactionModel);
        }
        this.displayedTableHeaders.set([...this.displayedColumns(), 'actions']);
    }

    triggerAllWinOlsRequest(): Observable<WinOlsTransactionPage> {
        return this.httpClient.get<WinOlsTransactionPage>(`${this.url}/winols/all`, {
            params: this.pageParams()
        });
    }

    getAllWinOlsTransactions(): void {
        this.loadTablePage(false);
    }

    setDateRange(startDate: string, endDate: string): void {
        this.selectedDateRange.set({startDate, endDate});
        this.selectedStatuses.set([]);
        this.startDate = undefined;
        this.endDate = undefined;
        this.status = undefined;
        this.resetPageNumber();
        this.winOlsTransactions.set([]);
        this.hasMorePages = true;
        this.loadTablePage(false);
    }

    clearDateRange(): void {
        this.selectedDateRange.set(null);
        this.selectedStatuses.set([]);
        this.startDate = undefined;
        this.endDate = undefined;
        this.status = undefined;
    }

    onScroll(): void {
        if (this.isFetchingPage || !this.hasMorePages || this.currentTransactionId !== null) {
            return;
        }
        this.increasePageNumber();
        this.loadTablePage(true);
    }

    increasePageNumber(): void {
        this.pageNumber.update((prevNum) => prevNum + 1);
    }

    changeSortDirection(sortDirection: SortDirectionValuesModel): void {
        this.sortDirectionValues.set(sortDirection);
        if (this.currentTransactionId !== null) {
            return;
        }
        this.resetPageNumber();
        this.hasMorePages = true;
        this.loadTablePage(false);
    }

    resetPageNumber(): void {
        this.pageNumber.set(0);
    }

    private pageParams() {
        return {
            pageNumber: this.pageNumber(),
            pageSize: this.pageSize(),
            sortAttribute: this.sortDirectionValues().active,
            sortDirection: this.sortDirectionValues().direction
        };
    }

    private requestTablePage(): Observable<WinOlsTransactionPage> {
        const range = this.selectedDateRange();
        const statuses = this.selectedStatuses();
        if (range && statuses.length) {
            const requestStatus = (status: string) => this.httpClient.get<WinOlsTransactionPage>(`${this.url}/winols/statusAndDateRange`, {
                params: {
                    ...this.pageParams(),
                    'start date': range.startDate,
                    'end date': range.endDate,
                    status
                }
            });
            if (statuses.length === 1) {
                return requestStatus(statuses[0]);
            }
            // The backend accepts one status per call. Keep both filtered pages together
            // for the combined Error / No Options segment.
            return forkJoin(statuses.map(requestStatus)).pipe(map(pages => {
                const content = this.sortTransactions(
                    pages.reduce<WinOlsTransactionModel[]>((records, page) => records.concat(page.content ?? []), [])
                );
                return {
                    content,
                    last: pages.every(page => page.last ?? (page.content ?? []).length < this.pageSize())
                };
            }));
        }
        if (range) {
            return this.httpClient.get<WinOlsTransactionPage>(`${this.url}/winols/transactions/date-range`, {
                params: {...this.pageParams(), startDate: range.startDate, endDate: range.endDate}
            });
        }
        return this.triggerAllWinOlsRequest();
    }

    private sortTransactions(records: WinOlsTransactionModel[]): WinOlsTransactionModel[] {
        const sort = this.sortDirectionValues();
        return records.sort((a, b) => {
            const left = String(a[sort.active as keyof WinOlsTransactionModel] ?? '');
            const right = String(b[sort.active as keyof WinOlsTransactionModel] ?? '');
            const comparison = left.localeCompare(right, undefined, {numeric: true});
            return sort.direction === 'asc' ? comparison : -comparison;
        });
    }

    private loadTablePage(append: boolean): void {
        this.currentTransactionId = null;
        const requestId = ++this.activeTableRequestId;
        this.isFetchingPage = true;
        if (!append) {
            this.isLoading.set(true);
        }
        this.requestTablePage().subscribe({
            next: page => {
                if (requestId !== this.activeTableRequestId) {
                    return;
                }
                const records = page.content ?? [];
                const displayedRecords = append ? [...this.winOlsTransactions(), ...records] : records;
                this.winOlsTransactions.set(this.selectedStatuses().length > 1
                    ? this.sortTransactions(displayedRecords)
                    : displayedRecords);
                this.hasMorePages = page.last === undefined ? records.length === this.pageSize() : !page.last;
                this.isFetchingPage = false;
                this.isLoading.set(false);
            },
            error: () => {
                if (requestId !== this.activeTableRequestId) {
                    return;
                }
                if (append) {
                    this.pageNumber.update(page => page - 1);
                } else {
                    this.winOlsTransactions.set([]);
                }
                this.isFetchingPage = false;
                this.isLoading.set(false);
                this.notificationService.showErrorMessage('Unable to load WinOLS transactions.', 'Error');
            }
        });
    }

    downloadFileFromTable(trace: string, id: number, route: string) {
        this.filesToDownload.update((prevIds) => [...prevIds, id]);

        const toastObj = this.notificationService.showInfoMessage(`Preparing your file with id: ${id}`, 'Preparing..');

        this.httpClient.post<{ url: string, message: string }>(`${this.url}/winols/${route}`, {}, {
            params: {
                trace: trace
            },
            observe: "response"
        }).subscribe(response => {

            const index = this.filesToDownload().indexOf(id);

            this.filesToDownload.update((files) => files.filter((file, i) => i !== index));

            if (response.status === 200) {
                this.notificationService.clear(toastObj.toastId);

                window.location.href = response.body.url;

                this.notificationService.showSuccessMessage(`<div><span>Your file with ID ${id} is downloaded</span></div>`, "Success");

            } else {
                this.notificationService.showErrorMessage('Something went wrong with file.', 'Failed');
            }
        })
    }

    isDownloadInProgress(id: number) {
        return this.filesToDownload().includes(id);
    }

    openPureOriginalMatchingDetails(pureOriginalId: number) {
        this.router.navigate(['app/pure-originals/pure-original-details'], {queryParams: {pureOriginalId: pureOriginalId}});
    }


    onRefreshTable(): void {
        this.clearDateRange();
        this.pageNumber.set(0);
        this.pageSize.set(50);
        this.sortDirectionValues.set({active: "id", direction: "desc"});
        this.winOlsTransactions.set([]);
        this.hasMorePages = true;
        this.loadTablePage(false);
    }

    resetTableHeaders() {
        this.displayedColumns.set([]);
        this.displayedTableHeaders.set([]);
        this.pageNumber.set(0);
        this.pageSize.set(50);
        this.displayedColumns.set([])
    }


    async getWinOlsTransactionsByDateRange(startDate: Date | string, endDate: Date | string) {
        const response = await firstValueFrom(this.httpClient.get<WinOlsStatisticModel[]>(`${this.url}/winols/dateRange`, {
            params: {
                'start date': typeof startDate === 'string' ? startDate : startDate.toISOString(),
                'end date': typeof endDate === 'string' ? endDate : endDate.toISOString(),
            }
        }))
        this.winOlsStatistic.set(response as WinOlsStatisticModel[]);
        return response;
    }

    getWinOlsDataRangeByStatus(startDate: string, endDate: string, status: WinOlsStatusFilter): void {
        this.selectedDateRange.set({startDate, endDate});
        this.selectedStatuses.set(status === 'ERROR_OR_NO_OPTIONS' ? ['ERROR', 'NO_AVAILABLE_OPTIONS'] : [status]);
        this.startDate = new Date(startDate);
        this.endDate = new Date(endDate);
        this.status = status;
        this.resetPageNumber();
        this.winOlsTransactions.set([]);
        this.hasMorePages = true;
        this.loadTablePage(false);
    }

    async getWinOlsStatistics(startDate: Date | string, endDate: Date | string) {

        await this.getWinOlsTransactionsByDateRange(startDate, endDate);

        let preCheck = 0;
        let requested = 0;
        let solutionOffered = 0;
        let noAvailableOptions = 0;
        let completed = 0;
        let error = 0;
        let total = 0;

        this.winOlsStatistic().forEach(winOlsStatistics => {
            switch (winOlsStatistics.winOlsTransactionStatus) {
                case 'PRE_CHECK':
                    preCheck += winOlsStatistics.count;
                    break;
                case 'REQUESTED':
                    requested += winOlsStatistics.count;
                    break;
                case 'SOLUTION_OFFERED':
                    solutionOffered += winOlsStatistics.count;
                    break;
                case 'NO_AVAILABLE_OPTIONS':
                    noAvailableOptions += winOlsStatistics.count;
                    break;
                case 'COMPLETED':
                    completed += winOlsStatistics.count;
                    break;
                case 'ERROR':
                    error += winOlsStatistics.count;
                    break;
                case 'TOTAL':
                    total += winOlsStatistics.count;
                    break;
                default:
                    break;
            }

        });
        this.preCheck.set(preCheck);
        this.requested.set(requested);
        this.solutionOffered.set(solutionOffered);
        this.noAvailableOptions.set(noAvailableOptions);
        this.completed.set(completed);
        this.error.set(error);
        this.total.set(preCheck + requested + solutionOffered + noAvailableOptions + completed + error);
    }

    async getWinOlsByDatePicker($event: any) {
        this.startDate = $event.valueOf().startDate;
        this.endDate = $event.valueOf().endDate;
        this.status = $event.valueOf().status;

        await this.getWinOlsStatistics(this.startDate, this.endDate);
    }

    getTransactionById(transactionId: number): void {
        this.clearDateRange();
        this.currentTransactionId = transactionId;
        this.hasMorePages = false;
        const requestId = ++this.activeTableRequestId;
        this.isFetchingPage = true;
        this.isLoading.set(true);

        this.httpClient.get<WinOlsTransactionModel>(`${this.url}/winols/transaction/${transactionId}`)
            .subscribe({
                next: response => {
                    if (requestId !== this.activeTableRequestId) {
                        return;
                    }
                    this.winOlsTransactions.set([response]);
                    this.isFetchingPage = false;
                    this.isLoading.set(false);
                },
                error: () => {
                    if (requestId !== this.activeTableRequestId) {
                        return;
                    }
                    this.winOlsTransactions.set([]);
                    this.isFetchingPage = false;
                    this.isLoading.set(false);
                    this.notificationService.showErrorMessage("Transaction not found", "Error");
                }
            });
    }

}






